(() => {
  document.documentElement.classList.add('aury-v10-js');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer:fine)').matches;

  const safeSearchUrl = (query, meta = {}) => {
    const cleaned = String(query || 'AURYMED').replace(/\s+/g, ' ').trim() || 'AURYMED';
    const params = new URLSearchParams();
    params.set('q', cleaned);
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    if (meta.for) params.set('aury_for', String(meta.for).toLowerCase());
    if (meta.kind) params.set('aury_kind', String(meta.kind).toLowerCase());
    if (meta.detail) params.set('aury_detail', String(meta.detail));
    if (meta.label) params.set('aury_label', String(meta.label));
    return `${window.Shopify?.routes?.root || '/'}search?${params.toString()}`;
  };

  const dedupe = (items) => [...new Set(items.map((item) => String(item || '').trim()).filter(Boolean))];

  // V24: one shared discovery profile for Finder, search ranking and recommendations.
  // Liquid writes the same profile fields onto every product card; all discovery features
  // consume those fields instead of inventing separate matching rules.
  const normalizeAury = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const auryProfile = (card) => ({
    world: normalizeAury(card?.dataset?.auryWorld),
    audience: normalizeAury(card?.dataset?.auryAudience),
    kind: normalizeAury(card?.dataset?.auryKind),
    handle: normalizeAury(card?.dataset?.auryHandle),
    text: normalizeAury(card?.dataset?.aurySearch)
  });

  const auryCompatibility = (candidate, target = {}, mode = 'recommendation', index = 0) => {
    let points = 0;
    const wanted = {
      world: normalizeAury(target.world),
      audience: normalizeAury(target.audience),
      kind: normalizeAury(target.kind),
      detail: normalizeAury(target.detail)
    };

    if (mode === 'finder') {
      if (wanted.kind) points += candidate.kind === wanted.kind ? 80 : -90;
      if (wanted.audience) points += candidate.audience === wanted.audience ? 65 : -75;
      if (wanted.world) points += candidate.world === wanted.world ? 18 : 0;
      if (wanted.detail) {
        const terms = wanted.detail.split(' ').filter((term) => term.length > 1);
        const hits = terms.filter((term) => candidate.text.includes(term)).length;
        points += hits * 12;
        if (terms.length && hits === terms.length) points += 18;
      }
      // VibraNex is intentionally not used as a generic filler for other product types.
      if (wanted.kind && wanted.kind !== 'vibranex' && candidate.kind === 'vibranex') points -= 55;
      return points - (index * 0.001);
    }

    if (wanted.kind && candidate.kind === wanted.kind) points += 8;
    if (wanted.audience && candidate.audience === wanted.audience) points += 6;
    if (wanted.world && candidate.world === wanted.world) points += 5;
    if (wanted.audience === 'tier' && candidate.audience !== 'tier') points -= 8;
    if (wanted.audience === 'raum' && candidate.audience !== 'raum') points -= 5;
    if (wanted.kind !== 'vibranex' && candidate.kind === 'vibranex') points -= 5;
    if (wanted.kind === 'vibranex' && candidate.kind !== 'vibranex') points -= 2;
    return points - (index * 0.01);
  };

  const buildFinderSignal = (state, typed = '') => {
    const who = state.for || '';
    const what = state.what || '';
    const detail = state.detail || '';
    const free = String(typed || '').trim();
    const labels = [];
    const whatLabel = what === 'Anhänger' ? 'Schmuck' : (what === 'Mystometrie' ? 'Form & Geometrie' : what);
    const kindMap = { 'Anhänger': 'schmuck', 'VibraNex': 'vibranex', 'Scheibe': 'scheibe', 'Raumobjekt': 'raumobjekt', 'Mystometrie': 'mystometrie' };

    if (who && who !== 'Offen') labels.push(who === 'Tier' ? 'Tierbegleiter' : who);
    if (whatLabel) labels.push(whatLabel);
    if (detail) labels.push(detail);
    if (free) labels.push(free);

    // V29: the browser URL now stays human-readable. We deliberately avoid exposing
    // Shopify's search grammar (OR / AND / title:) and use the shared AURYMED profile
    // to rank/filter results after Shopify returns a broad, understandable result set.
    const broadTerms = {
      'Anhänger': 'Anhänger',
      'VibraNex': 'VibraNex',
      'Scheibe': 'Scheibe',
      'Raumobjekt': 'Raum',
      'Mystometrie': 'Mystometrie'
    };

    let query = '';
    if (free) query = free;
    else if (detail) query = detail;
    else if (broadTerms[what]) query = broadTerms[what];
    else if (who === 'Tier') query = 'Tier';
    else if (who === 'Raum') query = 'Raum';
    else query = 'AURYMED';

    const status = labels.length ? `Deine Spur: ${labels.join(' · ')}` : 'Wähle, kombiniere oder tippe frei.';
    return {
      query,
      status,
      meta: {
        for: who && who !== 'Offen' ? who : '',
        kind: kindMap[what] || '',
        detail,
        label: labels.join(' · ') || 'AURYMED Auswahl'
      }
    };
  };

  const installScrollSignal = () => {
    if (document.querySelector('.aury-scroll-signal')) return;
    const signal = document.createElement('div');
    signal.className = 'aury-scroll-signal';
    signal.setAttribute('aria-hidden', 'true');
    document.body.appendChild(signal);
    const update = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      signal.style.setProperty('--aury-scroll', `${Math.min(1, Math.max(0, window.scrollY / max)) * 100}%`);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  };

  const installReveal = () => {
    const targets = document.querySelectorAll('.aury-portal__copy,.aury-portal__brand,.aury-finder__shell,.aury-world,.aury-vibranex__panel,.aury-manifesto__panel,.aury-collection-intro__inner,.aury-product-orbit__shell,.aury-product-compass__shell,.aury-product-uses__shell,.aury-signature-way__shell,.aury-constellation__shell,.aury-atlas__shell,.aury-atlas__route,.product-card');
    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('aury-inview'));
      return;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('aury-inview');
      observer.unobserve(entry.target);
    }), { threshold: 0.07, rootMargin: '0px 0px -4% 0px' });
    targets.forEach((el) => observer.observe(el));
  };

  const installPointerLight = () => {
    if (reduced || !finePointer) return;
    document.querySelectorAll('.aury-portal__brand,.aury-world,.product-card__content,.aury-finder__shell,.aury-constellation__shell,.aury-atlas__shell,.aury-atlas__route').forEach((el) => {
      el.addEventListener('pointermove', (event) => {
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--aury-x', `${event.clientX - rect.left}px`);
        el.style.setProperty('--aury-y', `${event.clientY - rect.top}px`);
        if (el.matches('.aury-constellation__shell')) {
          el.style.setProperty('--aury-cx', `${((event.clientX - rect.left) / rect.width) * 100}%`);
          el.style.setProperty('--aury-cy', `${((event.clientY - rect.top) / rect.height) * 100}%`);
        }
        if (el.matches('.aury-atlas__shell')) {
          el.style.setProperty('--atlas-x', `${event.clientX - rect.left}px`);
          el.style.setProperty('--atlas-y', `${event.clientY - rect.top}px`);
        }
        if (el.matches('.aury-atlas__route')) {
          el.style.setProperty('--route-x', `${event.clientX - rect.left}px`);
          el.style.setProperty('--route-y', `${event.clientY - rect.top}px`);
        }
      }, { passive: true });
    });
    document.querySelectorAll('.aury-constellation__node').forEach((node) => {
      node.addEventListener('pointermove', (event) => {
        const rect = node.getBoundingClientRect();
        node.style.setProperty('--node-x', `${event.clientX - rect.left}px`);
        node.style.setProperty('--node-y', `${event.clientY - rect.top}px`);
      }, { passive: true });
    });
  };

  const installPortalParallax = () => {
    if (reduced || !finePointer) return;
    const portal = document.querySelector('.aury-portal');
    if (!portal) return;
    const logo = portal.querySelector('.aury-portal__logo');
    const ring = portal.querySelector('.aury-portal__brand-ring');
    if (!logo || !ring) return;
    portal.addEventListener('pointermove', (event) => {
      const rect = portal.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
      logo.style.transform = `translate3d(${x * 8}px,${y * 6}px,0)`;
      ring.style.setProperty('--aury-parallax-x', `${x * -11}px`);
      ring.style.setProperty('--aury-parallax-y', `${y * -9}px`);
    }, { passive: true });
    portal.addEventListener('pointerleave', () => { logo.style.transform = ''; ring.style.setProperty('--aury-parallax-x','0px'); ring.style.setProperty('--aury-parallax-y','0px'); });
  };

  const installFinder = () => {
    document.querySelectorAll('[data-aury-finder]').forEach((finder) => {
      const state = { for: '', what: '', detail: '' };
      const input = finder.querySelector('[data-aury-finder-input]');
      const launch = finder.querySelector('[data-aury-finder-launch]');
      const status = finder.querySelector('[data-aury-finder-status]');
      const update = () => {
        const typed = input?.value.trim() || '';
        const signal = buildFinderSignal(state, typed);
        const hasChoice = Boolean(typed || state.for || state.what || state.detail);
        if (launch) {
          launch.href = hasChoice
            ? safeSearchUrl(signal.query, signal.meta)
            : `${window.Shopify?.routes?.root || '/'}collections/all`;
        }
        if (status) status.textContent = signal.status;
      };
      finder.querySelectorAll('.aury-finder__choices').forEach((group) => {
        group.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
          const key = group.dataset.group;
          const active = button.getAttribute('aria-pressed') === 'true';
          group.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed','false'));
          state[key] = active ? '' : button.dataset.value || '';
          if (!active) button.setAttribute('aria-pressed','true');
          update();
        }));
      });
      input?.addEventListener('input', update);
      finder.querySelector('[data-aury-surprise]')?.addEventListener('click', () => {
        const signals = ['VibraNex','Tier-Anhänger','Fell-Accessoires','Scheibe Coin','Armband Symbolschmuck','Raumobjekt','Metatron','Blume des Lebens','grün goldfarben','69 mm VibraNex'];
        const pick = signals[Math.floor(Math.random() * signals.length)];
        window.location.assign(safeSearchUrl(pick));
      });
      update();
    });
  };

  const installCleanSearchLanguage = () => {
    const input = document.querySelector('.search-page-input[data-aury-search-visible], .search-page-input');
    if (!input) return;
    const params = new URLSearchParams(window.location.search);
    const rawQuery = String(params.get('q') || '').trim();
    const technical = /\b(?:OR|AND)\b|title:|\-title:|\-\(title:/i.test(rawQuery);
    const labels = [];
    const explicitLabel = String(params.get('aury_label') || '').trim();
    const audience = normalizeAury(params.get('aury_for'));
    const kind = normalizeAury(params.get('aury_kind'));
    const detail = String(params.get('aury_detail') || '').trim();

    if (explicitLabel) {
      labels.push(explicitLabel);
    } else {
      if (audience === 'tier') labels.push('Tierwelt');
      else if (audience === 'raum') labels.push('Raum');
      else if (audience === 'mensch') labels.push('Für dich');
      const kindLabels = {
        schmuck: 'Symbolschmuck',
        vibranex: 'VibraNex',
        scheibe: 'Scheiben & Coins',
        raumobjekt: 'Raum & Stationen',
        mystometrie: 'Mystometrie'
      };
      if (kindLabels[kind]) labels.push(kindLabels[kind]);
      if (detail) labels.push(detail);
    }

    let friendly = labels.filter(Boolean).join(' · ');
    if (!friendly && technical) {
      if (/vibranex/i.test(rawQuery) && !/\b(?:OR|AND)\b/i.test(rawQuery)) friendly = 'VibraNex';
      else friendly = 'AURYMED Auswahl';
    }
    if (!friendly) return;

    input.value = friendly;
    input.dataset.auryFriendlyValue = friendly;
    input.dataset.auryRawQuery = rawQuery;
    if (technical) input.dataset.auryGeneratedFriendly = 'true';

    // Never expose Shopify search grammar to customers. This also covers browser
    // titles and any search UI that reuses the raw term outside the input.
    if (technical && rawQuery) {
      try {
        document.title = document.title.replace(rawQuery, friendly);
      } catch (_) {}
      document.querySelectorAll('.search-page__header h1, .search-page__header h2, .search-page__header h3, [data-search-term]').forEach((node) => {
        if (!node?.textContent?.includes(rawQuery)) return;
        node.textContent = node.textContent.replace(rawQuery, friendly);
      });
    }

    const noResults = document.querySelector('.search-results__no-results p');
    if (noResults && technical) noResults.textContent = `Für „${friendly}“ wurden gerade keine passenden Artikel gefunden.`;

    // If the visitor simply submits the generated friendly label again, keep the
    // actual Shopify query internal. Once they type something themselves, their
    // own wording is submitted normally.
    const form = input.closest('form[role="search"]');
    if (input.dataset.aurySearchBound !== 'true') {
      input.dataset.aurySearchBound = 'true';
      input.addEventListener('focus', () => {
        if (input.dataset.auryGeneratedFriendly === 'true' && input.value === input.dataset.auryFriendlyValue) {
          window.setTimeout(() => input.select(), 0);
        }
      });
      input.addEventListener('input', () => {
        if (input.value !== input.dataset.auryFriendlyValue) {
          delete input.dataset.auryRawQuery;
          delete input.dataset.auryGeneratedFriendly;
        }
      });
      form?.addEventListener('submit', () => {
        if (input.dataset.auryRawQuery && input.value === input.dataset.auryFriendlyValue) {
          input.value = input.dataset.auryRawQuery;
        }
      });
    }
  };

  const installSearchRerenderGuard = () => {
    if (!window.location.pathname.includes('/search')) return;
    const root = document.querySelector('#MainContent') || document.body;
    if (root.dataset.aurySearchObserver === 'true') return;
    root.dataset.aurySearchObserver = 'true';
    let queued = false;
    const refresh = () => {
      queued = false;
      installCleanSearchLanguage();
      installVibraSearchBrief();
    };
    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.type === 'childList' && (mutation.addedNodes.length || mutation.removedNodes.length))) return;
      if (queued) return;
      queued = true;
      requestAnimationFrame(refresh);
    });
    observer.observe(root, { childList: true, subtree: true });
  };

  const installVibraSearchBrief = () => {
    const brief = document.querySelector('[data-aury-vibranex-search-brief]');
    if (!brief) return;
    const params = new URLSearchParams(window.location.search);
    const raw = String(params.get('q') || '');
    const kind = normalizeAury(params.get('aury_kind'));
    const directVibraSearch = /vibranex/i.test(raw) && !/\b(?:OR|AND)\b/i.test(raw);
    if (kind === 'vibranex' || directVibraSearch) brief.hidden = false;
  };

  const installFinderResultGuard = () => {
    const params = new URLSearchParams(window.location.search);
    const wantedAudience = normalizeAury(params.get('aury_for'));
    const wantedKind = normalizeAury(params.get('aury_kind'));
    const wantedDetail = normalizeAury(params.get('aury_detail'));
    if (!wantedAudience && !wantedKind && !wantedDetail) return;

    let queued = false;
    const apply = () => {
      queued = false;
      const cards = [...document.querySelectorAll('product-card[data-aury-kind]')];
      if (!cards.length) return;

      const target = { audience: wantedAudience, kind: wantedKind, detail: wantedDetail };
      const rows = cards.map((card, index) => {
        const profile = auryProfile(card);
        const holder = card.closest('.product-grid__item, .resource-list__item, li') || card;
        const exactAudience = !wantedAudience || profile.audience === wantedAudience;
        const exactKind = !wantedKind || profile.kind === wantedKind;
        const exact = exactAudience && exactKind;
        return { card, holder, profile, exact, index, score: auryCompatibility(profile, target, 'finder', index) };
      });

      // Reset an earlier pass first; Shopify can re-render search results after filtering/sorting.
      rows.forEach(({ holder }) => {
        if (holder.dataset.auryFiltered === 'true') holder.hidden = false;
        delete holder.dataset.auryFiltered;
      });

      const exactRows = rows.filter((row) => row.exact);
      const useStrict = exactRows.length > 0 && (wantedAudience || wantedKind);
      rows.forEach((row) => {
        row.holder.dataset.auryFitScore = String(Math.round(row.score));
        if (useStrict && !row.exact) {
          row.holder.hidden = true;
          row.holder.dataset.auryFiltered = 'true';
        }
      });

      // Rank within each existing Shopify grid only; never move cards across pagination/containers.
      const groups = new Map();
      rows.filter((row) => !row.holder.hidden).forEach((row) => {
        const parent = row.holder.parentElement;
        if (!parent) return;
        if (!groups.has(parent)) groups.set(parent, []);
        groups.get(parent).push(row);
      });
      groups.forEach((group, parent) => {
        const sorted = group.sort((a, b) => b.score - a.score || a.index - b.index);
        const wanted = sorted.map((row) => row.holder);
        const present = [...parent.children].filter((child) => wanted.includes(child));
        const alreadyOrdered = wanted.length === present.length && wanted.every((holder, index) => present[index] === holder);
        if (!alreadyOrdered) wanted.forEach((holder) => parent.appendChild(holder));
      });
    };

    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    apply();
    const root = document.querySelector('#MainContent') || document.body;
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList' && (mutation.addedNodes.length || mutation.removedNodes.length))) schedule();
    });
    observer.observe(root, { childList: true, subtree: true });
  };


  const installSmartRecommendations = () => {
    document.querySelectorAll('product-recommendations[data-aury-smart-recommendations]').forEach((section) => {
      let queued = false;
      const apply = () => {
        queued = false;
        const lists = [...section.querySelectorAll('.resource-list')];
        if (!lists.length) return;
        const current = {
          world: normalizeAury(section.dataset.auryWorld),
          audience: normalizeAury(section.dataset.auryAudience),
          kind: normalizeAury(section.dataset.auryKind)
        };
        const limit = Math.max(1, Number(section.dataset.auryVisibleLimit || 4));

        lists.forEach((list) => {
          const items = [...list.querySelectorAll(':scope > .resource-list__item')];
          if (!items.length) return;
          const ranked = items
            .map((item, index) => {
              const card = item.querySelector('product-card[data-aury-kind]');
              const profile = card ? auryProfile(card) : null;
              return { item, index, score: profile ? auryCompatibility(profile, current, 'recommendation', index) : -100 - index };
            })
            .sort((a, b) => b.score - a.score || a.index - b.index);

          ranked.forEach(({ item, score }, index) => {
            item.hidden = index >= limit;
            item.dataset.auryRecommendationRank = String(index + 1);
            item.dataset.auryFitScore = String(Math.round(score));
          });
          const wantedOrder = ranked.map(({ item }) => item);
          const presentOrder = [...list.children].filter((child) => wantedOrder.includes(child));
          const alreadyOrdered = wantedOrder.length === presentOrder.length && wantedOrder.every((item, index) => presentOrder[index] === item);
          if (!alreadyOrdered) wantedOrder.forEach((item) => list.appendChild(item));
        });
        section.dataset.aurySmartReady = 'true';
      };

      const schedule = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(apply);
      };

      apply();
      const observer = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.type === 'childList' && mutation.addedNodes.length)) schedule();
      });
      observer.observe(section, { childList: true, subtree: true });
    });
  };


  const installUseGuides = () => {
    document.querySelectorAll('[data-aury-use-guide]').forEach((guide) => {
      const tabs = [...guide.querySelectorAll('[data-aury-use]')];
      const panels = [...guide.querySelectorAll('[data-aury-use-panel]')];
      if (!tabs.length || !panels.length) return;
      const guideId = guide.id || `aury-use-guide-${Math.random().toString(36).slice(2, 9)}`;
      if (!guide.id) guide.id = guideId;
      tabs.forEach((tab, index) => {
        const key = tab.dataset.auryUse || String(index);
        const tabId = `${guideId}-tab-${key}`;
        const panelId = `${guideId}-panel-${key}`;
        tab.id = tabId;
        tab.setAttribute('aria-controls', panelId);
        const panel = panels.find((item) => item.dataset.auryUsePanel === key);
        if (panel) {
          panel.id = panelId;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tabId);
        }
      });
      const activate = (key, focus = false) => {
        tabs.forEach((tab) => {
          const active = tab.dataset.auryUse === key;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus({ preventScroll: true });
        });
        panels.forEach((panel) => {
          const active = panel.dataset.auryUsePanel === key;
          panel.hidden = !active;
          panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab.dataset.auryUse || ''));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowRight','ArrowLeft','Home','End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          activate(tabs[next].dataset.auryUse || '', true);
        });
      });
      activate((tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]).dataset.auryUse || '');
    });
  };


  const installRecentOrbit = () => {
    const section = document.querySelector('[data-aury-recent-orbit]');
    if (!section) return;

    const STORAGE_KEY = 'aurymed_recent_orbit_v1';
    const MAX_STORED = 8;
    const MAX_VISIBLE = 4;
    const list = section.querySelector('[data-aury-recent-list]');
    const clear = section.querySelector('[data-aury-recent-clear]');

    const read = () => {
      try {
        const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(value) ? value.filter((item) => item && typeof item === 'object') : [];
      } catch (_) {
        return [];
      }
    };
    const write = (items) => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_STORED))); } catch (_) {}
    };
    const safePath = (value) => {
      if (!value || typeof value !== 'string') return '';
      try {
        const parsed = new URL(value, window.location.origin);
        return parsed.origin === window.location.origin ? `${parsed.pathname}${parsed.search}${parsed.hash}` : '';
      } catch (_) { return ''; }
    };
    const labelFor = (item) => {
      const kind = String(item.kind || '').toLowerCase();
      const audience = String(item.audience || '').toLowerCase();
      if (kind === 'vibranex') return 'VibraNex';
      if (audience === 'tier') return 'Tierwelt';
      if (kind === 'schmuck') return 'Symbolschmuck';
      if (kind === 'scheibe') return 'Scheiben & Coins';
      if (kind === 'raumobjekt') return 'Raum & Stationen';
      if (kind === 'mystometrie') return 'Mystometrie';
      return 'AURYMED';
    };
    const makeCard = (item) => {
      const href = safePath(item.url);
      if (!href) return null;
      const link = document.createElement('a');
      link.className = 'aury-recent-card';
      link.href = href;
      link.setAttribute('aria-label', String(item.title || 'Zuletzt angesehen'));

      const media = document.createElement('span');
      media.className = 'aury-recent-card__media';
      if (item.image) {
        const img = document.createElement('img');
        img.src = String(item.image);
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);
      }

      const body = document.createElement('span');
      body.className = 'aury-recent-card__body';
      const meta = document.createElement('span');
      meta.className = 'aury-recent-card__meta';
      meta.textContent = labelFor(item);
      const title = document.createElement('span');
      title.className = 'aury-recent-card__title';
      title.textContent = String(item.title || 'AURYMED Artikel');
      body.append(meta, title);
      if (item.price) {
        const price = document.createElement('span');
        price.className = 'aury-recent-card__price';
        price.textContent = String(item.price);
        body.appendChild(price);
      }
      link.append(media, body);
      return link;
    };

    const current = {
      handle: section.dataset.currentHandle || '',
      title: section.dataset.currentTitle || '',
      url: section.dataset.currentUrl || '',
      image: section.dataset.currentImage || '',
      price: section.dataset.currentPrice || '',
      world: section.dataset.currentWorld || '',
      audience: section.dataset.currentAudience || '',
      kind: section.dataset.currentKind || '',
      seenAt: Date.now()
    };

    const stored = read();
    const previous = stored.filter((item) => item.handle && item.handle !== current.handle).slice(0, MAX_VISIBLE);
    if (list && previous.length) {
      list.replaceChildren(...previous.map(makeCard).filter(Boolean));
      if (list.children.length) section.hidden = false;
    }

    if (current.handle && safePath(current.url)) {
      write([current, ...stored.filter((item) => item.handle !== current.handle)]);
    }

    clear?.addEventListener('click', () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      section.hidden = true;
      if (list) list.replaceChildren();
    });
  };



  // V25: usability guardrails. Keep orientation without adding another visible section.
  const installBackToBrowse = () => {
    const back = document.querySelector('[data-aury-back-browse]');
    if (!back || !document.referrer) return;
    try {
      const ref = new URL(document.referrer);
      if (ref.origin !== window.location.origin) return;
      if (!/(\/collections(?:\/|$)|\/search(?:\?|$))/.test(ref.pathname + ref.search)) return;
      back.href = `${ref.pathname}${ref.search}${ref.hash}`;
      back.hidden = false;
    } catch (_) {}
  };

  const installDiscoveryRescue = () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('aury_for') && !params.get('aury_kind') && !params.get('aury_detail')) return;
    const root = document.querySelector('results-list, .search-results, .search-page, #MainContent');
    if (!root) return;
    const render = () => {
      if (document.querySelector('.aury-search-rescue, [data-aury-native-empty]')) return;
      const cards = [...document.querySelectorAll('product-card[data-aury-kind]')].filter((card) => {
        const holder = card.closest('.product-grid__item, .resource-list__item, li') || card;
        return !holder.hidden;
      });
      if (cards.length) return;
      const rescue = document.createElement('div');
      rescue.className = 'aury-search-rescue page-width';
      rescue.setAttribute('role','status');
      rescue.innerHTML = `<div class="aury-search-rescue__inner"><strong>Diese Spur ist gerade leer.</strong><span>Kein Problem – du kannst die Auswahl lockern oder wieder alles ansehen.</span><div><a href="${safeSearchUrl('AURYMED')}">Suche öffnen</a><a href="${window.Shopify?.routes?.root || '/'}collections/all">Alle Artikel</a></div></div>`;
      root.prepend(rescue);
    };
    window.setTimeout(render, 700);
  };

  const installProductAnchors = () => {
    const details = document.querySelector('product-information .product-details, .product-information .product-details, product-information [data-testid="product-details"]');
    if (details && !document.getElementById('aury-product-details')) {
      const anchor = document.createElement('span');
      anchor.id = 'aury-product-details';
      anchor.className = 'aury-anchor-target';
      anchor.setAttribute('aria-hidden', 'true');
      details.prepend(anchor);
    }

    document.querySelectorAll('.aury-product-compass__nav a[href^="#"]').forEach((link) => {
      if (link.dataset.auryAnchorReady === 'true') return;
      link.dataset.auryAnchorReady = 'true';
      link.addEventListener('click', (event) => {
        const selector = link.getAttribute('href');
        if (!selector || selector === '#') return;
        const target = document.querySelector(selector);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        try { history.replaceState(null, '', selector); } catch (_) {}
      });
    });
  };

  const installProductMediaLight = () => {
    if (reduced || !finePointer) return;
    document.querySelectorAll('.product-information .product-media-gallery, product-information [data-testid="product-media-gallery"]').forEach((gallery) => {
      gallery.classList.add('aury-media-light');
      gallery.addEventListener('pointermove', (event) => {
        const r = gallery.getBoundingClientRect();
        gallery.style.setProperty('--media-x', `${event.clientX-r.left}px`);
        gallery.style.setProperty('--media-y', `${event.clientY-r.top}px`);
      }, { passive:true });
    });
  };

  const installOrbitDock = () => {
    if (!document.body.matches(':has(.aury-portal)') || document.querySelector('.aury-orbit-dock')) return;
    const dock = document.createElement('nav');
    dock.className = 'aury-orbit-dock';
    dock.setAttribute('aria-label','AURYMED Schnellnavigation');
    dock.innerHTML = `
      <a href="#MainContent" aria-label="Nach oben"><span>✦</span></a>
      <a href="#aury-worlds">Welten</a>
      <a href="#aury-finder">Finder</a>
      <a href="${window.Shopify?.routes?.root || '/'}collections/all">Shop</a>
      <a href="${window.Shopify?.routes?.root || '/'}cart" aria-label="Warenkorb">Warenkorb</a>`;
    document.body.appendChild(dock);
  };

  const boot = () => {
    installScrollSignal();
    installReveal();
    installPointerLight();
    installPortalParallax();
    installFinder();
    installCleanSearchLanguage();
    installSearchRerenderGuard();
    installVibraSearchBrief();
    installFinderResultGuard();
    installSmartRecommendations();
    installUseGuides();
    installRecentOrbit();
    installBackToBrowse();
    installDiscoveryRescue();
    installProductAnchors();
    installProductMediaLight();
    installOrbitDock();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once:true }); else boot();
})();

/* AURYMED V43 — restrained shop motion: no sound, no confetti. */
(() => {
  const initAuryV43 = () => {
    document.documentElement.classList.add('js');
    document.body?.classList.add('aury-page-enter');

    let signal = document.getElementById('aury-scroll-signal');
    if (!signal) {
      signal = document.createElement('div');
      signal.id = 'aury-scroll-signal';
      signal.setAttribute('aria-hidden', 'true');
      document.body?.appendChild(signal);
    }

    let ticking = false;
    const updateSignal = () => {
      ticking = false;
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - window.innerHeight);
      const p = Math.min(1, Math.max(0, window.scrollY / max));
      if (signal) signal.style.transform = `scaleX(${p})`;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateSignal);
      }
    }, { passive: true });
    window.addEventListener('resize', updateSignal, { passive: true });
    updateSignal();

    const revealTargets = document.querySelectorAll([
      '.aury-worlds', '.aury-finder', '.aury-atlas', '.aury-vibranex',
      '.aury-signature-way', '.aury-manifesto', '.aury-constellation',
      '.aury-product-orbit', '.aury-product-compass', '.aury-product-uses',
      '.aury-collection-intro', '.aurymed-product-description',
      '.shopify-section .product-list', '.shopify-section .collection-list'
    ].join(','));

    revealTargets.forEach(el => el.classList.add('aury-reveal'));

    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });
      revealTargets.forEach(el => io.observe(el));
    } else {
      revealTargets.forEach(el => el.classList.add('is-visible'));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAuryV43, { once: true });
  } else {
    initAuryV43();
  }
})();
